import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { AuthenticatedUser } from "@/types/domain.types";
import type { UserRole } from "@/types/enums";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  authUser: AuthenticatedUser | null;
  isSample: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  authUser: null,
  isSample: false,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

/** Parse JWT claims from session to get role/company/shop/staff */
function parseAuthUser(session: Session | null): AuthenticatedUser | null {
  if (!session?.user) return null;
  const claims = session.access_token
    ? JSON.parse(atob(session.access_token.split(".")[1]))
    : {};

  // Prefer JWT custom claim, fall back to app_metadata.role (root identification)
  const appMetaRole = (session.user.app_metadata as { role?: string } | undefined)?.role;
  const role = (claims.user_role as UserRole) ?? (appMetaRole as UserRole) ?? "user";
  return {
    id: session.user.id,
    email: session.user.email ?? "",
    role,
    company_id: claims.company_id ?? null,
    shop_id: claims.shop_id ?? null,
    staff_id: claims.staff_id ?? null,
    isSample: false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [dbRole, setDbRole] = useState<{
    role: UserRole;
    company_id: string | null;
    shop_id: string | null;
    staff_id: string | null;
  } | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fallback: fetch role from accounts table (joined with staff for shop_id) when JWT claims might not have it.
  // Root users are identified via auth.users.raw_app_meta_data and have no row in accounts.
  useEffect(() => {
    if (!session?.user?.id) {
      setDbRole(null);
      return;
    }
    const appMetaRole = (session.user.app_metadata as { role?: string } | undefined)?.role;
    if (appMetaRole === "root") {
      setDbRole({ role: "root", company_id: null, shop_id: null, staff_id: null });
      return;
    }
    (supabase as any)
      .from("accounts")
      .select("role, company_id, staff_id, staff:staff_id(shop_id)")
      .eq("auth_uid", session.user.id)
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (data) {
          setDbRole({
            role: data.role as UserRole,
            company_id: data.company_id ?? null,
            shop_id: data.staff?.shop_id ?? null,
            staff_id: data.staff_id ?? null,
          });
        }
      });
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setDbRole(null);
  };

  const jwtUser = parseAuthUser(session);
  const authUser: AuthenticatedUser | null = jwtUser
    ? {
        ...jwtUser,
        role: dbRole?.role ?? jwtUser.role,
        company_id: dbRole?.company_id ?? jwtUser.company_id,
        shop_id: dbRole?.shop_id ?? jwtUser.shop_id,
        staff_id: dbRole?.staff_id ?? jwtUser.staff_id,
      }
    : null;
  const isSample = false;

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, authUser, isSample, loading, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** Route guard — redirects to /login if unauthenticated */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !session) {
      navigate("/login", { replace: true });
    }
  }, [loading, session, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold tracking-wider">SATTOU.</h1>
          <p className="text-sm text-muted-foreground">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!session) return null;

  return <>{children}</>;
}
